import { useMutation } from "@tanstack/react-query";
import { axiosInstance } from "@retro-ia/shared/axios";

export interface EvaluateArchivoOptions {
  file: string | undefined;
  file_clean: string;
  index: string;
  programa: number;
  actividad: number;
}

export function useEvaluateArchivo() {
  return useMutation<RetroSessionPayload, unknown, EvaluateArchivoOptions>({
    mutationFn: async (options) => {
      const res = await axiosInstance.post("/retro-api/evaluate", {
        file: options.file,
        file_clean: options.file_clean,
        index: options.index,
        programa: options.programa,
        actividad: options.actividad,
      });
      return res.data.data as RetroSessionPayload;
    },
  });
}

export interface RetroSessionPayload {
  session_id: string;
  session_payload: string;
  base_feedback: string;
  evaluation_source: string;
  template_source: string;
  evaluation_chunks: string[];
  retrieved_contexts: string[];
  suggested_questions?: string[];
  metadata?: Record<string, unknown>;
  generated_at: string;
  compute_time_seconds: number;
  vector_index?: string;
}

export interface ValidationError {
  valid: false;
  error_code: "INVALID_DOCUMENT";
  message: string;
}

export interface StreamErrorEvent {
  type: "error";
  step?: string;
  error_code?: "INVALID_DOCUMENT";
  message: string;
  code?: number;
}

export function useRetroSession() {
  return useMutation<RetroSessionPayload, unknown, string>({
    mutationFn: async (sessionId) => {
      const res = await axiosInstance.get(`/retro-api/session/${sessionId}`);
      return res.data as RetroSessionPayload;
    },
  });
}

export interface StreamCallbacks {
  onChunk?: (chunk: string) => void;
  onComplete?: (payload: RetroSessionPayload) => void;
  onError?: (error: Error) => void;
  onMessage?: (message: string) => void;
}

export async function evaluateArchivoStream(
  options: EvaluateArchivoOptions,
  callbacks: StreamCallbacks,
  signal?: AbortSignal
): Promise<void> {
  const baseURL = axiosInstance.defaults.baseURL || '';
  const url = `${baseURL}/retro-api/evaluate`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        file: options.file,
        file_clean: options.file_clean,
        index: options.index,
        programa: options.programa,
        actividad: options.actividad,
      }),
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No se pudo obtener el reader del stream');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      
      // Procesar líneas completas, dejar la última incompleta en el buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (!data.trim()) continue;

          try {
            const event = JSON.parse(data);
            
            if (event.type === 'chunk' && event.content) {
              callbacks.onChunk?.(event.content);
            } else if (event.type === 'progress' && event.message) {
              callbacks.onMessage?.(event.message);
            } else if (event.type === 'done' && event.payload) {
              callbacks.onComplete?.(event.payload as RetroSessionPayload);
            } else if (event.type === 'error') {
              // Crear un error especial para errores de validación de documento
              const streamError = event as StreamErrorEvent;
              // Detectar errores de validación por step o error_code
              if (streamError.step === 'validation' || streamError.error_code === 'INVALID_DOCUMENT') {
                const validationError = new Error(streamError.message || 'El documento subido no corresponde a la evaluación esperada.');
                (validationError as any).error_code = 'INVALID_DOCUMENT';
                (validationError as any).isValidationError = true;
                throw validationError;
              }
              throw new Error(streamError.message || 'Error en el stream');
            }
          } catch (parseError) {
            // Si es un error de validación, propagarlo
            if (parseError instanceof Error && (parseError as any).isValidationError) {
              throw parseError;
            }
            console.error('Error parsing SSE data:', parseError);
          }
        }
      }
    }
  } catch (error) {
    if (error instanceof Error) {
      callbacks.onError?.(error);
    } else {
      callbacks.onError?.(new Error('Error desconocido'));
    }
  }
}
